import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Paper, Stack, IconButton, CircularProgress, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { CVHttpService } from '../../services';
import LanguageFormDialog from '../dialogs/LanguageFormDialog';

function LanguagesStep({ cvId }) {
    const [languages, setLanguages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingLanguage, setEditingLanguage] = useState(null);

    useEffect(() => {
        if (cvId) {
            loadLanguages();
        }
    }, [cvId]);

    const loadLanguages = async () => {
        if (!cvId) return;
        setLoading(true);
        try {
            const service = new (await import('core/services/http')).HttpService();
            const data = await service.get(`/job/cv/userlanguage/?cv=${cvId}`, null);
            // Handle both array response and paginated response
            const languagesList = Array.isArray(data) ? data : (data?.results || []);
            setLanguages(languagesList);
        } catch (err) {
            console.error('Failed to load languages', err);
            setLanguages([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this language?')) return;
        try {
            await CVHttpService.deleteLanguage(id);
            loadLanguages();
        } catch (err) {
            alert('Failed to delete language');
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight={600}>
                    Languages
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setEditingLanguage(null);
                        setOpenDialog(true);
                    }}
                >
                    Add Language
                </Button>
            </Stack>

            {!Array.isArray(languages) || languages.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <Typography color="text.secondary">
                        No languages added yet. Click "Add Language" to get started.
                    </Typography>
                </Paper>
            ) : (
                <Stack spacing={2}>
                    {Array.isArray(languages) && languages.map((lang) => (
                        <Paper key={lang.id} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="start">
                                <Box sx={{ flexGrow: 1 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                        <Typography variant="h6" fontWeight={600}>
                                            {lang.title}
                                        </Typography>
                                        {lang.proficiency && (
                                            <Chip 
                                                label={lang.proficiency} 
                                                size="small" 
                                                color="primary" 
                                                variant="outlined"
                                            />
                                        )}
                                    </Stack>
                                </Box>
                                <Stack direction="row" spacing={1}>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setEditingLanguage(lang);
                                            setOpenDialog(true);
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDelete(lang.id)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            )}

            <LanguageFormDialog
                open={openDialog}
                onClose={() => {
                    setOpenDialog(false);
                    setEditingLanguage(null);
                }}
                onSave={() => {
                    loadLanguages();
                    setOpenDialog(false);
                    setEditingLanguage(null);
                }}
                cvId={cvId}
                language={editingLanguage}
            />
        </Box>
    );
}

export default LanguagesStep;

